import { useCallback, useEffect, useState } from 'react'

import { useQueryClient } from '@tanstack/react-query'

import { fetchCommunityModelLike } from 'Pods/Shared/CommunityModelLike/communityModelLikeApi'
import patchCommunityGalleryLikeCache from 'Pods/Shared/CommunityModelLike/patchCommunityGalleryLikeCache'
import useCommunityModelLike from 'Pods/Shared/CommunityModelLike/useCommunityModelLike'

// Owns the viewer's like state for the opened model. The initial `liked` flag is
// user-specific and never SSR'd on this fast page, so it is resolved client-side
// via GET; likeCount is seeded from the model payload (the community serializers
// expose it) to avoid a flash, then reconciled from the server. isLoaded turns true
// once that read has answered for the current slug (or failed, leaving the seed), for
// callers that must not toggle against a state that is not this model's yet.
export default function useModelLike({ slug, initialCount = 0, dailyLimitMessage = '' }) {
  const queryClient = useQueryClient()
  const [isLiked, setIsLiked] = useState(false)
  const [likeCount, setLikeCount] = useState(initialCount || 0)
  const [loadedSlug, setLoadedSlug] = useState(null)

  const applyPayload = useCallback((payload) => {
    if (typeof payload?.liked === 'boolean') {
      setIsLiked(payload.liked)
    }

    if (typeof payload?.likeCount === 'number') {
      setLikeCount(payload.likeCount)
    }

    // Mirror the resolved state into the community-gallery cache so a like made
    // in the footer shows the same count on the gallery cards, which read from
    // that cache and do not refetch on navigation.
    patchCommunityGalleryLikeCache(queryClient, {
      slug,
      liked: payload?.liked,
      likeCount: payload?.likeCount,
    })
  }, [queryClient, slug])

  const { toggleLike, isToggling } = useCommunityModelLike({
    onLikeChange: applyPayload,
    dailyLimitMessage,
  })

  useEffect(() => {
    if (!slug) return undefined

    let cancelled = false

    // A failed read leaves the seeded count in place: an unreadable like state is
    // not worth an error surface, the toggle reconciles from the server anyway.
    fetchCommunityModelLike(slug)
      .then((payload) => {
        if (!cancelled) applyPayload(payload)
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setLoadedSlug(slug)
      })

    return () => {
      cancelled = true
    }
  }, [slug, applyPayload])

  const handleToggleLike = useCallback(() => {
    toggleLike({ slug, liked: isLiked, likeCount })
  }, [toggleLike, slug, isLiked, likeCount])

  return {
    isLiked, likeCount, isToggling, isLoaded: loadedSlug === slug, toggleLike: handleToggleLike,
  }
}
